// Word's clipboard HTML, cleaned down to the formatting a document keeps (TMS-1884):
// paragraphs, bold, italics, one level of sub-heading, bulleted and numbered lists, and
// links. Fonts, colours, sizes and Word's own markup are dropped. The rich text editor
// runs this on paste only for a host that opts in (`paste_cleanup: "word"`).
//
// Two things Word does need undoing rather than stripping:
// - its lists are not lists: each item is a paragraph styled `mso-list:l0 level1 lfo1`
//   whose bullet or number is a literal character in a `mso-list:Ignore` span;
// - it breaks one bold run into several (`<b>Dr</b><b>. Smith</b>`), which would read
//   as separate bold words once the spans between them are gone.
//
// Word Online and Google Docs paste real lists and styled spans, which the same
// element rules cover. The server sanitizes again on save; this is what the author
// sees in the editor before then.

const DROPPED_ELEMENTS = new Set(["SCRIPT", "STYLE", "META", "LINK", "TITLE", "XML", "O:P", "HEAD"])
const BLOCK_TAGS = {
  P: "p", DIV: "p", BLOCKQUOTE: "p", TD: "p", TH: "p",
  H1: "h3", H2: "h3", H3: "h3", H4: "h3", H5: "h3", H6: "h3",
  UL: "ul", OL: "ol", LI: "li"
}
const MERGEABLE_TAGS = new Set(["STRONG", "EM"])
const NESTED_BLOCKS = "p, div, h1, h2, h3, h4, h5, h6, ul, ol, li, table, blockquote"
const WORD_LIST_STYLE = /mso-list:\s*(l\d+)/i
const WORD_LIST_MARKER_STYLE = /mso-list:\s*ignore/i
const ORDERED_MARKER = /^\(?[0-9a-z]{1,4}[.)]/i

export function cleanWordPaste(html) {
  const source = new DOMParser().parseFromString(html, "text/html").body
  rebuildWordLists(source)

  const output = document.createElement("div")
  appendCleanChildren(source, output)
  mergeSplitFormatting(output)
  removeEmptyBlocks(output)

  return output.innerHTML
}

// Groups each run of Word list paragraphs into a real list, nesting by Word's level. A run
// is adjacent paragraphs of one Word list (`l0`, `l1`…), so a bulleted list followed straight
// by a numbered one stays two lists.
function rebuildWordLists(root) {
  const paragraphs = Array.from(root.querySelectorAll("p")).filter((paragraph) =>
    WORD_LIST_STYLE.test(paragraph.getAttribute("style") || "")
  )
  let run = null

  paragraphs.forEach((paragraph) => {
    const listId = WORD_LIST_STYLE.exec(paragraph.getAttribute("style"))[1]
    const level = wordListLevel(paragraph)
    const tagName = ORDERED_MARKER.test(takeWordListMarker(paragraph)) ? "ol" : "ul"

    if (!run || run.listId !== listId || paragraph.previousElementSibling !== run.root) {
      const list = root.ownerDocument.createElement(tagName)
      paragraph.before(list)
      run = { listId, root: list, stack: [{ list, level }] }
    }

    while (run.stack.length > 1 && level < run.stack[run.stack.length - 1].level) run.stack.pop()

    let current = run.stack[run.stack.length - 1]
    if (level > current.level) {
      const nested = root.ownerDocument.createElement(tagName)
      ;(current.list.lastElementChild || current.list).appendChild(nested)
      current = { list: nested, level }
      run.stack.push(current)
    }

    const item = root.ownerDocument.createElement("li")
    item.append(...paragraph.childNodes)
    current.list.appendChild(item)
    paragraph.remove()
  })
}

function wordListLevel(paragraph) {
  const match = /level(\d+)/i.exec(paragraph.getAttribute("style") || "")
  return match ? Number(match[1]) : 1
}

// Removes the bullet/number Word typed into the paragraph and returns its text.
function takeWordListMarker(paragraph) {
  const markers = Array.from(paragraph.querySelectorAll("span")).filter((span) =>
    WORD_LIST_MARKER_STYLE.test(span.getAttribute("style") || "")
  )
  const text = markers.map((marker) => marker.textContent).join("").trim()
  markers.forEach((marker) => marker.remove())
  return text
}

function appendCleanChildren(source, target) {
  source.childNodes.forEach((node) => appendClean(node, target))
}

function appendClean(node, target) {
  if (node.nodeType === Node.TEXT_NODE) {
    // Word wraps its source at a fixed width; those line breaks are not the author's.
    target.appendChild(document.createTextNode(node.textContent.replace(/[\r\n]+/g, " ")))
    return
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return

  const tagName = node.tagName.toUpperCase()
  if (DROPPED_ELEMENTS.has(tagName)) return
  if (tagName === "BR") return target.appendChild(document.createElement("br"))

  let container = target
  const blockTag = paragraphWrapper(node) ? null : BLOCK_TAGS[tagName]
  if (blockTag) container = appendElement(container, blockTag)

  if (tagName === "A" && node.getAttribute("href")) {
    container = appendElement(container, "a")
    container.setAttribute("href", node.getAttribute("href"))
  }
  if (isBold(node)) container = appendElement(container, "strong")
  if (isItalic(node)) container = appendElement(container, "em")

  appendCleanChildren(node, container)
}

// Word Online wraps each paragraph in a <div>, and a table cell can hold several
// paragraphs: such a wrapper is unwrapped rather than made a <p> around blocks, which
// the browser would split into empty paragraphs on either side.
function paragraphWrapper(element) {
  return BLOCK_TAGS[element.tagName.toUpperCase()] === "p" && element.querySelector(NESTED_BLOCKS) !== null
}

function appendElement(parent, tagName) {
  const element = document.createElement(tagName)
  parent.appendChild(element)
  return element
}

function isBold(element) {
  const weight = declaredStyle(element, "font-weight")
  if (weight) return weight === "bold" || weight === "bolder" || Number(weight) >= 600

  return ["B", "STRONG"].includes(element.tagName.toUpperCase())
}

function isItalic(element) {
  const style = declaredStyle(element, "font-style")
  if (style) return style === "italic" || style === "oblique"

  return ["I", "EM"].includes(element.tagName.toUpperCase())
}

// The value an element's own style attribute declares, lower-cased — Google Docs wraps a
// whole paste in <b style="font-weight:normal">, which must not read as bold.
function declaredStyle(element, property) {
  const match = new RegExp(`(?:^|;)\\s*${property}\\s*:\\s*([^;]+)`, "i").exec(element.getAttribute("style") || "")
  return match ? match[1].trim().toLowerCase() : null
}

// One run of bold that Word split, or that whitespace between two bold words split
// once the spans around it were dropped, becomes one <strong>.
function mergeSplitFormatting(root) {
  root.querySelectorAll("strong, em").forEach((element) => {
    // Already merged into the run before it.
    if (!element.parentNode) return

    let next = element.nextSibling
    while (next) {
      const whitespace = next.nodeType === Node.TEXT_NODE && next.textContent.trim() === ""
      const following = whitespace ? next.nextSibling : next
      if (!following || following.nodeName !== element.nodeName || !MERGEABLE_TAGS.has(following.nodeName)) break

      if (whitespace) element.appendChild(next)
      element.append(...following.childNodes)
      following.remove()
      next = element.nextSibling
    }
  })
}

// Word marks a blank line as a paragraph holding only a non-breaking space.
function removeEmptyBlocks(root) {
  root.querySelectorAll("p, h3, li").forEach((block) => {
    if (block.textContent.replace(/ /g, " ").trim() === "" && !block.querySelector("br")) block.remove()
  })
  root.querySelectorAll("strong, em").forEach((element) => {
    if (element.textContent === "") element.remove()
  })
}
